import { useMemo } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import {
  Eye,
  EyeOff,
  MousePointer2,
  PenLine,
  Square,
  Crop,
  X,
  Highlighter,
  Sparkles,
  StickyNote,
  Stamp,
} from 'lucide-react'
import { useProjectStore, deriveOrderedPages } from '../store/projectStore.js'
import PageThumbnail from './PageThumbnail.jsx'

const TOOLS = [
  { id: 'select', label: 'Select', icon: MousePointer2, hint: 'Tap pages to inspect or open.', shortcut: 'V' },
  { id: 'stamp', label: 'Stamp', icon: Stamp, hint: 'Tap to drop centered text.', shortcut: 'S' },
  { id: 'redact', label: 'Redact', icon: Square, hint: 'Drag to draw a permanent black rectangle.', shortcut: 'R' },
  { id: 'crop', label: 'Crop', icon: Crop, hint: 'Drag to define a crop region (one per page).', shortcut: 'C' },
  { id: 'highlight', label: 'Highlight', icon: Highlighter, hint: 'Drag to mark a region with translucent color.', shortcut: 'H' },
  { id: 'ink', label: 'Ink', icon: PenLine, hint: 'Free-hand draw on the page.', shortcut: 'I' },
  { id: 'sticky', label: 'Note', icon: StickyNote, hint: 'Tap to add a sticky note marker.', shortcut: 'N' },
  { id: 'signature', label: 'Sign', icon: Sparkles, hint: 'Drag to place the selected signature.', shortcut: 'G' },
]

export default function CenterWorkspace() {
  const documents = useProjectStore((s) => s.documents)
  const pageOrder = useProjectStore((s) => s.pageOrder)
  const reorderPages = useProjectStore((s) => s.reorderPages)
  const toolMode = useProjectStore((s) => s.toolMode)
  const setToolMode = useProjectStore((s) => s.setToolMode)
  const setAllPagesIncluded = useProjectStore((s) => s.setAllPagesIncluded)
  const stampSettings = useProjectStore((s) => s.stampSettings)
  const setStampSettings = useProjectStore((s) => s.setStampSettings)
  const annotationSettings = useProjectStore((s) => s.annotationSettings)
  const setAnnotationSettings = useProjectStore((s) => s.setAnnotationSettings)
  const signatures = useProjectStore((s) => s.signatures)

  const pages = useMemo(
    () => deriveOrderedPages(documents, pageOrder),
    [documents, pageOrder]
  )
  const includedCount = useMemo(
    () => pages.filter((p) => p.included).length,
    [pages]
  )

  const handleDragEnd = (result) => {
    if (!result.destination) return
    if (result.destination.index === result.source.index) return
    const next = Array.from(pageOrder)
    const [moved] = next.splice(result.source.index, 1)
    next.splice(result.destination.index, 0, moved)
    reorderPages(next)
  }

  const activeTool = TOOLS.find((t) => t.id === toolMode) || TOOLS[0]
  const ActiveIcon = activeTool.icon
  const isDrawing = ['redact', 'crop', 'highlight', 'ink', 'sticky', 'signature'].includes(toolMode)

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2.5 sm:px-5 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-800">
            {TOOLS.map((tool) => {
              const Icon = tool.icon
              const isActive = toolMode === tool.id
              return (
                <button
                  key={tool.id}
                  type="button"
                  onClick={() => setToolMode(tool.id)}
                  title={`${tool.label} (${tool.shortcut}) — ${tool.hint}`}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium transition sm:gap-1.5 sm:px-3 sm:text-xs ${
                    isActive
                      ? 'bg-white text-slate-900 shadow-soft dark:bg-slate-700 dark:text-slate-100'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden xs:inline sm:inline">{tool.label}</span>
                </button>
              )
            })}
          </div>

          {toolMode === 'stamp' && (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1 shadow-soft dark:border-slate-700 dark:bg-slate-800">
              <input
                type="text"
                value={stampSettings.text}
                onChange={(e) => setStampSettings({ text: e.target.value })}
                placeholder="Stamp text"
                className="w-32 border-0 bg-transparent text-sm text-slate-800 placeholder-slate-400 outline-none dark:text-slate-100"
              />
              <span className="h-4 w-px bg-slate-200 dark:bg-slate-700" />
              <input
                type="number"
                min={6}
                max={96}
                value={stampSettings.size}
                onChange={(e) =>
                  setStampSettings({
                    size: Math.max(6, Math.min(96, Number(e.target.value) || 16)),
                  })
                }
                className="w-12 rounded border border-slate-200 bg-transparent px-1.5 py-0.5 text-xs dark:border-slate-700 dark:text-slate-100"
                title="Size"
              />
              <input
                type="color"
                value={stampSettings.color}
                onChange={(e) => setStampSettings({ color: e.target.value })}
                className="h-6 w-6 cursor-pointer rounded border border-slate-200 dark:border-slate-700"
                title="Color"
              />
            </div>
          )}

          {toolMode === 'highlight' && (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1 shadow-soft dark:border-slate-700 dark:bg-slate-800">
              <span className="text-[11px] text-slate-500">Color</span>
              <input
                type="color"
                value={annotationSettings.highlightColor}
                onChange={(e) => setAnnotationSettings({ highlightColor: e.target.value })}
                className="h-6 w-6 cursor-pointer rounded border border-slate-200 dark:border-slate-700"
                title="Highlight color"
              />
            </div>
          )}

          {toolMode === 'ink' && (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1 shadow-soft dark:border-slate-700 dark:bg-slate-800">
              <span className="text-[11px] text-slate-500">Ink</span>
              <input
                type="color"
                value={annotationSettings.inkColor}
                onChange={(e) => setAnnotationSettings({ inkColor: e.target.value })}
                className="h-6 w-6 cursor-pointer rounded border border-slate-200 dark:border-slate-700"
                title="Ink color"
              />
              <input
                type="number"
                min={1}
                max={20}
                value={annotationSettings.inkSize}
                onChange={(e) =>
                  setAnnotationSettings({
                    inkSize: Math.max(1, Math.min(20, Number(e.target.value) || 2)),
                  })
                }
                className="w-12 rounded border border-slate-200 bg-transparent px-1.5 py-0.5 text-xs dark:border-slate-700 dark:text-slate-100"
                title="Stroke size"
              />
            </div>
          )}

          {toolMode === 'sticky' && (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1 shadow-soft dark:border-slate-700 dark:bg-slate-800">
              <span className="text-[11px] text-slate-500">Note color</span>
              <input
                type="color"
                value={annotationSettings.stickyColor}
                onChange={(e) => setAnnotationSettings({ stickyColor: e.target.value })}
                className="h-6 w-6 cursor-pointer rounded border border-slate-200 dark:border-slate-700"
                title="Note color"
              />
            </div>
          )}

          {toolMode === 'signature' && (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1 shadow-soft dark:border-slate-700 dark:bg-slate-800">
              <span className="text-[11px] text-slate-500">Signature</span>
              <select
                value={annotationSettings.signatureId || ''}
                onChange={(e) =>
                  setAnnotationSettings({ signatureId: e.target.value || null })
                }
                className="rounded border border-slate-200 bg-transparent px-1.5 py-0.5 text-xs dark:border-slate-700 dark:text-slate-100"
              >
                <option value="">— pick —</option>
                {signatures.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {signatures.length === 0 && (
                <span className="text-[10px] text-amber-600">Add one in the inspector</span>
              )}
            </div>
          )}

          {isDrawing && (
            <span className="hidden items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-600 ring-1 ring-slate-200 lg:inline-flex dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
              <ActiveIcon className="h-3 w-3" />
              {activeTool.hint}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden text-xs text-slate-500 sm:inline dark:text-slate-400">
            {includedCount}/{pages.length} pages
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setAllPagesIncluded(true)}
              className="btn-ghost h-8 px-2 text-xs dark:text-slate-200 dark:hover:bg-slate-800"
              title="Include all"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setAllPagesIncluded(false)}
              className="btn-ghost h-8 px-2 text-xs dark:text-slate-200 dark:hover:bg-slate-800"
              title="Exclude all"
            >
              <EyeOff className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-slate-100/60 px-3 py-4 sm:px-5 sm:py-5 dark:bg-slate-950">
        {pages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            No pages yet.
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="pages" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] sm:gap-4"
                >
                  {pages.map((page, index) => (
                    <Draggable
                      draggableId={page.id}
                      index={index}
                      key={page.id}
                      isDragDisabled={isDrawing || toolMode === 'stamp'}
                    >
                      {(prov, snapshot) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className={`relative ${snapshot.isDragging ? 'z-30' : ''}`}
                          style={prov.draggableProps.style}
                        >
                          <PageThumbnail
                            page={page}
                            index={index}
                            isDragging={snapshot.isDragging}
                            dragHandleProps={prov.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  )
}
